import { useMutation } from "@apollo/client";
import { Button, toast } from "@repo/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/yieldhub-sync")({
  head: () => opsHead("Sync Yieldhub"),
  component: View,
});

function View() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const [createYieldHubFile] = useMutation(
    graphql(`
      mutation CreateYieldHubFile(
        $input: CreateYieldHubFileInput!
      ) {
        createYieldHubFile(input: $input) {
          yieldHubFile {
            id
          }
          uploadUrl
        }
      }
    `),
  );

  const handleFile = async (file: File) => {
    try {
      setUploading(true);

      const result = await createYieldHubFile({
        variables: {
          input: { fileName: file.name, contentType: file.type },
        },
      });

      const payload = result.data?.createYieldHubFile;
      const fileId = payload?.yieldHubFile?.id;
      if (!payload || !fileId) {
        throw new Error("expected yieldHubFile in payload");
      }

      const uploadResponse = await fetch(payload.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      void navigate({
        to: "/yieldhub-sync/$fileId",
        params: { fileId },
      });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: String(err),
        kind: "error",
        autoHide: false,
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title="Sync Yieldhub"
        subtitle="Upload an HTML file of the Yieldhub Curve rates table."
      />
      <div
        className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-2 border-dashed bg-canvas transition-colors data-[dragging=true]:border-brand-highlight data-[dragging=true]:bg-secondary"
        data-dragging={isDraggingOver}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDraggingOver(true);
        }}
        onDragLeave={() => setIsDraggingOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDraggingOver(false);
          const file = e.dataTransfer.files[0];
          if (file) {
            void handleFile(file);
          }
        }}
      >
        <p className="text-secondary text-sm">
          Drag and drop an HTML file here, or
        </p>
        <Button
          variant="outline"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? "Uploading…" : "Choose file"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="text/html"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              void handleFile(file);
            }
          }}
        />
      </div>
    </AdminPageContainer>
  );
}
